/**
 * Home - Homepage component
 * Static homepage with hero section, platform information, and featured prompts
 * Responsive design with mobile-first approach and call-to-action elements
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthState } from '../context/AuthContext';
import Button from '../components/ui/Button';
import config from '../config';
import FeatureDemoSection from '../components/home/FeatureDemoSection';
import LoginEncouragementSection from '../components/home/LoginEncouragementSection';
import SharePlatformCTA from '../components/home/SharePlatformCTA';
import InteractiveSiteTour from '../components/home/InteractiveSiteTour';
import SEO from '../components/seo/SEO';

export default function Home() {
  const { isAuthenticated } = useAuthState();
  const [isTourOpen, setIsTourOpen] = useState(false);

  return (
    <div className="min-h-screen responsive-container" data-testid="home-container">
      <SEO 
        title="PromptShare - Discover and Share the Best AI Prompts"
        description="Share, discover, and create incredible AI prompts. Join our community of creators and explore the endless possibilities of artificial intelligence."
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-50 to-primary-100 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Discover Amazing{' '}
              <span className="text-primary-600">AI Prompts</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Share, discover, and create incredible AI prompts. Join our community of creators 
              and explore the endless possibilities of artificial intelligence.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => setIsTourOpen(true)}
                size="lg"
                className="px-8 flex items-center justify-center gap-2"
              >
                🎓 Take Guided Tour
              </Button>
              {!isAuthenticated ? (
                <>
                  <Link to="/register">
                    <Button size="lg" className="px-8">
                      Get Started
                    </Button>
                  </Link>
                  <Link to="/prompts">
                    <Button variant="outline" size="lg" className="px-8">
                      Browse Prompts
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/prompts/create">
                    <Button size="lg" className="px-8">
                      Create Prompt
                    </Button>
                  </Link>
                  <Link to="/prompts">
                    <Button variant="outline" size="lg" className="px-8">
                      Browse Prompts
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-20 h-20 bg-primary-200 rounded-full opacity-50 animate-pulse"></div>
          <div className="absolute top-40 right-20 w-16 h-16 bg-primary-300 rounded-full opacity-30 animate-pulse delay-1000"></div>
          <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-primary-400 rounded-full opacity-40 animate-pulse delay-500"></div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Choose {config.APP.NAME}?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to create, share, and discover amazing AI prompts
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Create & Share</h3>
              <p className="text-gray-600">
                Easily create and share your AI prompts with our intuitive interface. 
                Help others discover amazing possibilities.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Discover</h3>
              <p className="text-gray-600">
                Browse through thousands of curated prompts from our community. 
                Find inspiration for your next project.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Community</h3>
              <p className="text-gray-600">
                Join a vibrant community of AI enthusiasts. Like, comment, and collaborate 
                on amazing prompts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Prompts Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Featured Prompts
            </h2>
            <p className="text-lg text-gray-600">
              Discover some of the most popular prompts from our community
            </p>
          </div>

          {/* Static Featured Prompts for Phase 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Sample Prompt 1 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              <div className="aspect-video bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                <svg className="w-16 h-16 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Stunning Landscape Photography
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Create breathtaking landscape photos with perfect lighting and composition...
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      124
                    </span>
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      23
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">2 days ago</span>
                </div>
              </div>
            </div>

            {/* Sample Prompt 2 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              <div className="aspect-video bg-gradient-to-br from-green-100 to-teal-100 flex items-center justify-center">
                <svg className="w-16 h-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Creative Writing Assistant
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Generate compelling stories and creative content with AI assistance...
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      89
                    </span>
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      15
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">1 week ago</span>
                </div>
              </div>
            </div>

            {/* Sample Prompt 3 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
              <div className="aspect-video bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                <svg className="w-16 h-16 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V6a2 2 0 00-2-2H7z" />
                </svg>
              </div>
              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Business Plan Generator
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Create comprehensive business plans and strategies with AI guidance...
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      67
                    </span>
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      8
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">3 days ago</span>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/prompts">
              <Button variant="outline" size="lg">
                View All Prompts
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-primary-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Join thousands of creators sharing amazing AI prompts
          </p>
          
          {!isAuthenticated ? (
            <Button variant="secondary" size="lg" className="px-8">
              Sign Up Now
            </Button>
          ) : (
            <Link to="/prompts/create">
              <Button variant="secondary" size="lg" className="px-8">
                Create Your First Prompt
              </Button>
            </Link>
          )}
        </div>
      </section>

      {/* Shared feature demo + login/share CTAs */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50 space-y-12">
        <div className="max-w-7xl mx-auto space-y-12">
          <FeatureDemoSection isAuthenticated={isAuthenticated} />
          {!isAuthenticated && <LoginEncouragementSection />}
          <SharePlatformCTA />
        </div>
      </section>

      {/* Collapsible FAQ Section for users & SEO crawlers */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-200">
        <div className="max-w-4xl mx-auto">
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What is PromptShare?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "PromptShare is a premier community-driven platform for discovering, copying, and sharing high-quality AI prompts for models like Midjourney, ChatGPT, DALL-E, Stable Diffusion, and more."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How do I copy prompts on PromptShare?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Simply browse to any prompt details page, and click the 'Copy Prompt' button. You can then paste it directly into ChatGPT, Midjourney, or any other AI client."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Can I share my own AI prompts?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, absolutely! Anyone can join for free, create prompts, upload visual previews, categorize them, and get feedback and likes from the community."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is PromptShare free to use?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, PromptShare is 100% free to search, discover, copy, and share AI prompts."
                  }
                }
              ]
            })}
          </script>

          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Frequently Asked Questions</h3>
          <div className="space-y-4">
            <FAQItem
              q="What is PromptShare?"
              a="PromptShare is a premier community-driven platform for discovering, copying, and sharing high-quality AI prompts for models like Midjourney, ChatGPT, DALL-E, Stable Diffusion, and more."
            />
            <FAQItem
              q="How do I copy prompts on PromptShare?"
              a="Simply browse to any prompt details page, and click the 'Copy Prompt' button. You can then paste it directly into ChatGPT, Midjourney, or any other AI client."
            />
            <FAQItem
              q="Can I share my own AI prompts?"
              a="Yes, absolutely! Anyone can join for free, create prompts, upload visual previews, categorize them, and get feedback and likes from the community."
            />
            <FAQItem
              q="Is PromptShare free to use?"
              a="Yes, PromptShare is 100% free to search, discover, copy, and share AI prompts."
            />
          </div>
        </div>
      </section>

      {/* Custom premium onboarding tour */}
      <InteractiveSiteTour isOpen={isTourOpen} onClose={() => setIsTourOpen(false)} />
    </div>
  );
}

function FAQItem({ q, a }: { q: string; a: string }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-gray-200 pb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center text-left py-3 font-medium text-gray-900 hover:text-primary-600 transition-colors"
      >
        <span>{q}</span>
        <span className="ml-6 flex-shrink-0 text-gray-500 font-bold text-lg">
          {isOpen ? '−' : '+'}
        </span>
      </button>
      {isOpen && (
        <div className="mt-2 text-gray-600 text-sm leading-relaxed transition-all duration-300">
          {a}
        </div>
      )}
    </div>
  );
}